import React, { useMemo } from 'react'
import { getAMLScore as getAMLScoreData } from '@/api/Clients/Types/ClientAmlRiskLevelType'
import { Badge } from '@/components/ui/badge'
import { useAppSelector } from '@/store'
import { ClientAMLDetails } from 'api/Clients/Types/ClientAMLDetails'
import { Input } from 'components/Input'
import styles from 'styles/components/QuestionAnswerSection/questionAnswerSection.module.scss'

import { SectionContainer } from './questionAnswerSection'

export type AMLField = ClientAMLDetails['amlFields'][number]
export type AMLFieldValue = AMLField['values'][number]

interface AmlFieldAnswerSectionProps {
  label: string
  items: AMLField[]
  showScore: boolean
  sectionScore?: number | null

  groupByType?: boolean
  isNewWindow?: boolean
}

export const getAmlFieldValueLabel = (v?: AMLFieldValue | null) =>
  v?.amlFieldScore?.displayName || v?.value || '-'

export const getAmlFieldValueScore = (v?: AMLFieldValue | null) =>
  v?.score ?? v?.amlFieldScore?.score ?? 0

const isTextAreaValue = (value: string) => value.length > 100

const FieldRow: React.FC<{
  field: AMLField
  showScore: boolean
}> = ({ field, showScore }) => {
  const value = field.currentValue ?? null
  const label = getAmlFieldValueLabel(value)
  const score = getAmlFieldValueScore(value)

  return (
    <div>
      <span>{field.displayName}</span>

      <div className={styles.inputWrapper}>
        {isTextAreaValue(label) ? (
          <textarea value={label} disabled rows={3} />
        ) : (
          <Input value={label} type='text' disabled />
        )}

        {showScore && <span className={styles.score}>Score: {score}</span>}
      </div>
    </div>
  )
}

export const AmlFieldAnswerSection: React.FC<AmlFieldAnswerSectionProps> = ({
  label,
  items,
  showScore,
  sectionScore = null,
  groupByType = true,
  isNewWindow,
}) => {
  const amlCategories = useAppSelector((state) => state.clients.amlCategories)

  const computedTotalScore = useMemo(
    () => items.reduce((sum, f) => sum + getAmlFieldValueScore(f.currentValue), 0),
    [items]
  )

  const displayedSectionScore = sectionScore ?? computedTotalScore

  const amlScore = useMemo(
    () => getAMLScoreData(amlCategories)(displayedSectionScore),
    [amlCategories, displayedSectionScore]
  )

  const grouped = useMemo(() => {
    if (!groupByType) return null

    const groups: Record<string, { typeLabel: string; fields: AMLField[] }> = {}

    items.forEach((field) => {
      let key = String(field.amlFieldType?.id ?? field.amlFieldType?.name ?? 'Other')
      let typeLabel = field.amlFieldType?.name || 'Other'

      if (field.reference === 'non_face_to_face') {
        key = 'non_face_to_face'
        typeLabel = 'Non Face-to-face'
      } else if (field.reference === 'dual_citizenship' || typeLabel === 'Nationality') {
        key = 'nationality'
        typeLabel = 'Nationality'
      }

      if (typeLabel === 'Test') return

      if (!groups[key]) {
        groups[key] = { typeLabel, fields: [] }
      }
      groups[key].fields.push(field)
    })

    return Object.values(groups)
      .map((g) => ({
        ...g,
        fields:
          g.typeLabel === 'Nationality'
            ? [...g.fields].sort((a, b) => {
                if (a.reference === 'dual_citizenship') return 1
                if (b.reference === 'dual_citizenship') return -1
                return 0
              })
            : g.fields,
      }))
      .sort((a, b) => a.typeLabel.localeCompare(b.typeLabel))
  }, [items, groupByType])

  if (groupByType && grouped) {
    return (
      <>
        {showScore && (
          <div className='flex gap-2'>
            <span style={{ fontWeight: 600 }}>Total AML Score:</span>
            <Badge
              variant='default'
              style={{
                background: amlScore.color,
                color: '#FFF',
              }}
            >
              {displayedSectionScore}
            </Badge>
            <span style={{ color: amlScore.color, fontWeight: 600 }}>{amlScore.name}</span>
          </div>
        )}

        {grouped.map((g) => (
          <SectionContainer key={g.typeLabel} isNewWindow={isNewWindow}>
            <label>{g.typeLabel}</label>

            {g.fields.length === 0 && <span>No data</span>}

            {g.fields.map((field) => (
              <FieldRow key={field.id} field={field} showScore={showScore} />
            ))}
          </SectionContainer>
        ))}
      </>
    )
  }

  return (
    <SectionContainer isNewWindow={isNewWindow}>
      <label>{label}</label>

      {items.length === 0 && <span>No data</span>}

      {items.map((field) => (
        <FieldRow key={field.id} field={field} showScore={showScore} />
      ))}

      {showScore && (
        <span className={styles.totalScore}>Section score: {displayedSectionScore}</span>
      )}
    </SectionContainer>
  )
}
